import { flash, Modal, SubmitButton } from "@xola/ui-kit";
import { nullableNumber, requiredString } from "common";
import { isNull, omit, sortBy } from "lodash";
import React, { useState } from "react";
import { useMutation } from "react-query";
import { Except } from "type-fest";
import {
    AddOn,
    AmountType,
    CatalogItemDisplaySection,
    CatalogItemObject,
    CatalogItemRestriction,
    CatalogItemVisibility,
    CatalogType,
    Experience,
    Form,
    SelectedExperiences,
    UnitType,
} from "types";
import { array, number, object, string } from "yup";
import { SecondaryButton } from "../../../Common/SecondaryButton";
import { XolaFormik } from "../../../Common/XolaFormik";
import {
    getRestrictionError,
    getRestrictionsForType,
    hasQuestionnaireDisplaySection,
    updateAddOnFormFields,
} from "./AddOnFormUtils";
import { AddOnEditorFormState } from "./AddOnTypes";
import { EditAddOnFields } from "./EditAddOnFields";
import { OverrideQuestionnaireModal } from "./OverrideQuestionnaireModal";
import { api } from "@/api/api";
import { uploadFile } from "@/api/mediaApi";
import { xolaUri } from "@/api/xolaUri";
import { config } from "@/config";
import { useForm, useSaveForm, useSaveQuestionnaireForSavedViews } from "@/hooks/questionnaireFormHooks";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { sanitizeExperiencePayload } from "@/models/Experience";
import { getSelectionState } from "@/models/SelectionState";
import { hasX2CheckoutWithFullProbability } from "@/models/Seller";

interface EditGlobalAddOnModalProps {
    addOn?: AddOn;
    isOpen: boolean;
    onClose: () => void;
    experience?: Experience;
    associatedExperiences?: Experience[];
    currentPageExperience?: Experience; // This prop used to avoid linking addon request duplicate when we choose the same product as experienceId in url in Products modal
    afterCreate?: (addOn: AddOn, formValues: AddOnEditorFormState) => Promise<any>;
    isQuestionnaire?: boolean;
}

export const EditAddOnModal = ({
    addOn,
    isOpen,
    onClose,
    experience,
    associatedExperiences,
    currentPageExperience,
    afterCreate,
    isQuestionnaire,
}: EditGlobalAddOnModalProps) => {
    return (
        <Modal size="huge" isOpen={isOpen} onClose={onClose}>
            <ModalContent
                addOn={addOn}
                experience={experience}
                associatedExperiences={associatedExperiences}
                currentPageExperience={currentPageExperience}
                afterCreate={afterCreate}
                isQuestionnaire={isQuestionnaire}
                onClose={onClose}
            />
        </Modal>
    );
};

const validationSchema = object().shape({
    restrictions: array().test("limits", "", function (value) {
        // yup's inferred array type does not match CatalogItemRestriction, so cast it.
        const restrictions = getRestrictionsForType(this.parent.type, value as unknown as CatalogItemRestriction[]);
        const failing = restrictions
            .map((restriction) => getRestrictionError(restrictions, restriction.scope))
            .find(Boolean);

        return failing ? this.createError({ message: failing }) : true;
    }),
    name: requiredString(),
    caption: string(),
    type: string(),
    price: number().when("type", {
        is: CatalogType.CHOICES,
        then: nullableNumber(),
        otherwise: number().min(0).typeError("must be a number").required(),
    }),
    defaultChoice: string(),
    visibility: string(),
    priceType: requiredString(),
    items: array()
        .of(
            object().shape({
                name: requiredString(),
                price: number().min(0).typeError("must be a number").required(),
            }),
        )
        .optional(),
});

const setDefaultChoiceAfterCreation = async (createdAddOn: AddOn, values: AddOnEditorFormState) => {
    const selectedChoiceIndex = values.items.findIndex((item) => item.id === values.defaultChoice);

    if (selectedChoiceIndex >= 0 && createdAddOn.items?.[selectedChoiceIndex]?.id) {
        const actualChoiceId = createdAddOn.items[selectedChoiceIndex].id;
        await api.put(`/catalogItems/${createdAddOn.id}`, {
            ...createdAddOn,
            defaultChoice: actualChoiceId,
        });
    }
};

const ModalContent = ({
    addOn,
    onClose,
    experience,
    associatedExperiences,
    currentPageExperience,
    afterCreate,
    isQuestionnaire,
}: Except<EditGlobalAddOnModalProps, "isOpen">) => {
    const seller = useSeller();
    const invalidate = useInvalidate();
    const [picture, setPicture] = useState<File | undefined | null>();
    const [isOverrideModalOpen, setIsOverrideModalOpen] = useState(false);
    const [pendingFormValues, setPendingFormValues] = useState<AddOnEditorFormState | null>(null);
    const { form: questionnaireForm, globalForm: globalQuestionnaire } = useForm(experience?.id);
    const { mutateAsync: saveForm } = useSaveForm();
    const saveQuestionnaireForSavedViews = useSaveQuestionnaireForSavedViews();
    const currentField = questionnaireForm?.fields.find(
        (field) => field.catalogItem && field.catalogItem.id === addOn?.id,
    );
    const requireGuestIdentification =
        experience || currentPageExperience
            ? experience?.requireGuestIdentification ?? currentPageExperience?.requireGuestIdentification
            : false;
    const showTargetAudience = hasX2CheckoutWithFullProbability() && requireGuestIdentification;

    const { mutateAsync: upsertGlobalAddOn, isLoading } = useMutation(async (payload) => {
        if (addOn?.id) {
            return api.put<AddOn>(`/catalogItems/${addOn.id}`, payload);
        }

        return api.post<AddOn>("/catalogItems", payload);
    });

    const upsertPicture = async (data?: AddOn) => {
        if (picture) {
            const url = `${config.xola.url}/api/catalogItems/${data?.id}/media?seller=${seller.id}`;
            await uploadFile(url, picture, { "X-API-VERSION": config.xola.apiVersion });
        }

        setPicture(undefined);
    };

    const experienceList = associatedExperiences?.map((experience) => experience.id) ?? [];
    const selectedExperiences: SelectedExperiences = { all: false, experiences: experienceList };

    const linkAddOnToExperiences = async (addOn: AddOn, experienceIds: string[]) => {
        // Link add-ons to newly selected experiences
        const linkExperienceFiltered = experienceIds.filter(
            (experienceId) => !selectedExperiences.experiences.includes(experienceId),
        );

        for (const experienceId of linkExperienceFiltered) {
            if (experienceId !== currentPageExperience?.id) {
                await linkAddOnToExperience({ addOn, experienceId });
            }
        }

        // Unlink add-ons from removed experiences
        const unlinkExperienceFiltered =
            associatedExperiences?.filter((experience) => !experienceIds.includes(experience.id)) ?? [];

        for (const experience of unlinkExperienceFiltered) {
            if (associatedExperiences) {
                const catalogItem = experience.catalog.items.find((catalogItem) => catalogItem.parent?.id === addOn.id);
                if (catalogItem?.id) {
                    await unlinkAddOnToExperience({ id: catalogItem.id });
                }
            }
        }
    };

    const { mutateAsync: linkAddOnToExperience, isLoading: isLinkingAddOn } = useMutation(
        async ({ addOn, experienceId }: { addOn: AddOn; experienceId: string }) => {
            await api.post(`/catalogItems`, {
                object: CatalogItemObject.PRODUCT_CATALOG_ITEM,
                product: { id: experienceId },
                parent: { id: addOn.id },
                seller: { id: seller.id },
            });
        },
    );

    const { mutateAsync: unlinkAddOnToExperience } = useMutation(async ({ id }: { id: ID }) => {
        await api.delete(`/catalogItems/${id}`);
    });

    const executeSave = async (values: AddOnEditorFormState) => {
        const payload: any = {
            name: values.name,
            type: values.type,
            unitType: UnitType.ADDON,
            object: CatalogItemObject.GLOBAL_CATALOG_ITEM,
            price: values.price,
            visibility: values.visibility,
            seller: { id: seller.id },
            caption: values.caption,
            priceType: values.priceType,
            required: values.required,
            restrictions: getRestrictionsForType(values.type, values.restrictions),
            quantitySource: values.quantitySource,
        };

        if (values.type === CatalogType.CHOICES) {
            payload.items = values.items.map((item) => {
                if ((item.id?.length ?? 0) < 24) {
                    // The BE returns us an id of 24 characters. We remove the ids made on the FE side.
                    return omit(item, "id");
                }

                return item;
            });
        }

        if (isNull(picture)) {
            payload.photo = null;
        }

        if (experience) {
            const addOnValues = { ...payload };
            addOnValues.object = CatalogItemObject.PRODUCT_CATALOG_ITEM;
            addOnValues.product = { id: experience.id };
            addOnValues.overrideParent = true;
            addOnValues.parent = addOn?.parent;

            // The API checks the audience on the stored add-on, which changes only after the questionnaire drops the
            // field. So the first save leaves the guest-count match off, even one inherited from the global add-on.
            const isLeavingQuestionnaire =
                !!addOn?.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE) &&
                !values.displaySections.includes(CatalogItemDisplaySection.QUESTIONNAIRE);
            const shouldDeferMatch = isLeavingQuestionnaire && values.quantitySource !== null;

            const firstSavePayload = shouldDeferMatch ? { ...addOnValues, quantitySource: null } : addOnValues;

            await upsertPicture(addOn);

            await upsertGlobalAddOn(firstSavePayload);
            // Update form field roster settings if this is an existing addon with questionnaire
            if (addOn?.id) {
                // Create updated addon object with new displaySections from form values
                const updatedAddOn = { ...addOn, displaySections: values.displaySections ?? null };
                await updateAddOnFormFields({
                    catalogItem: updatedAddOn,
                    formValues: values,
                    globalQuestionnaire,
                    questionnaireForm,
                    experience,
                    saveForm,
                    saveQuestionnaireForSavedViews,
                });
            }

            if (shouldDeferMatch) {
                await upsertGlobalAddOn(addOnValues);
            }
            await api.put(`/experiences/${experience?.id}`, sanitizeExperiencePayload(experience));

            invalidate("catalogItems", addOn?.id);
            invalidate("experiences", experience?.id);
            invalidate("purchaseRuleLinks");
            flash.success("Add-on saved");
            onClose();
            return;
        }

        if (values.type === CatalogType.CHOICES) {
            payload.defaultChoice = values.defaultChoice;
        }

        const { data } = await upsertGlobalAddOn(payload);
        const updatedData = { ...data, displaySections: values.displaySections ?? null };

        await upsertPicture(updatedData);

        const shouldUpdateDefaultChoice = values.type === CatalogType.CHOICES && values.defaultChoice;
        // For new dropdown add-ons, set the default choice after creation if one was selected
        if (!addOn?.id && shouldUpdateDefaultChoice) {
            await setDefaultChoiceAfterCreation(data, values);
        }

        await linkAddOnToExperiences(updatedData, values.products.selected);

        if (afterCreate) {
            await afterCreate(updatedData, values);
        }

        invalidate("experiences");
        invalidate("catalogItems");
        invalidate("purchaseRuleLinks");
        flash.success("Add-on saved");
        onClose();
    };

    const handleSubmit = async (values: AddOnEditorFormState) => {
        const isSwitchingToAttendee =
            values.displaySections.includes(CatalogItemDisplaySection.QUESTIONNAIRE) &&
            !addOn?.displaySections?.includes(CatalogItemDisplaySection.QUESTIONNAIRE);

        const isUsingGlobal = !!globalQuestionnaire && !questionnaireForm;
        if (experience && isSwitchingToAttendee && isUsingGlobal) {
            setPendingFormValues(values);
            setIsOverrideModalOpen(true);
            return;
        }

        await executeSave(values);
    };

    const getImageUrl = () => {
        if (isNull(picture)) {
            // image has been deleted
            return "";
        }

        if (picture) {
            return URL.createObjectURL(picture);
        }

        if (addOn?.photo?.src) {
            return `${xolaUri}${addOn?.photo.src}`;
        }

        return "";
    };

    const handleImageDelete = () => {
        setPicture(null);
    };

    const isImageChanged = addOn?.photo?.src ? `${xolaUri}${addOn?.photo?.src}` !== getImageUrl() : !!getImageUrl();

    return (
        <XolaFormik
            enableReinitialize
            initialValues={getInitialValues(selectedExperiences, addOn, questionnaireForm, isQuestionnaire)}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={handleSubmit}
        >
            {({ handleSubmit, dirty, isSubmitting }) => {
                return (
                    <>
                        <OverrideQuestionnaireModal
                            isOpen={isOverrideModalOpen}
                            onClose={() => {
                                setIsOverrideModalOpen(false);
                                setPendingFormValues(null);
                            }}
                            onConfirm={async () => {
                                if (pendingFormValues) {
                                    await executeSave(pendingFormValues);
                                }
                            }}
                        />
                        <Modal.Header>{addOn ? "Edit" : "New"} Add-On</Modal.Header>
                        <Modal.Body>
                            <EditAddOnFields
                                showTargetAudience={showTargetAudience}
                                addOn={addOn}
                                imageUrl={getImageUrl()}
                                isProductAddon={experience || currentPageExperience ? true : false}
                                field={currentField}
                                isQuestionnaire={isQuestionnaire}
                                onImageChange={(file: File) => setPicture(file)}
                                onImageDelete={handleImageDelete}
                            />
                        </Modal.Body>
                        <Modal.Footer>
                            <SecondaryButton
                                onClick={() => {
                                    onClose();
                                }}
                            >
                                Cancel
                            </SecondaryButton>

                            <SubmitButton
                                isLoading={isLoading || isSubmitting || isLinkingAddOn}
                                disabled={!(dirty || isImageChanged)}
                                onClick={handleSubmit}
                            >
                                Save
                            </SubmitButton>
                        </Modal.Footer>
                    </>
                );
            }}
        </XolaFormik>
    );
};

const getInitialValues = (
    selectedExperiences: SelectedExperiences,
    addOn?: AddOn,
    questionnaireForm?: Form,
    isQuestionnaire?: boolean,
): AddOnEditorFormState => {
    let rosterSettings = { roster: false, addToSavedViews: false };

    // If editing an existing addon with questionnaire display section, load roster settings from form field
    if (addOn?.id && hasQuestionnaireDisplaySection(addOn) && questionnaireForm) {
        const correspondingField = questionnaireForm.fields.find((field) => field.catalogItem?.id === addOn.id);
        if (correspondingField) {
            rosterSettings = {
                roster: correspondingField.roster ?? false,
                addToSavedViews: correspondingField.addToSavedViews ?? false,
            };
        }
    }

    // Default to guest selection when creating new addon from questionnaire page
    let defaultDisplaySections = addOn?.displaySections ?? [];
    if (isQuestionnaire && !addOn?.id) {
        defaultDisplaySections = [CatalogItemDisplaySection.QUESTIONNAIRE];
    }

    return {
        name: addOn?.name ?? "",
        caption: addOn?.caption ?? "",
        visibility: addOn?.visibility ?? CatalogItemVisibility.PUBLIC,
        required: addOn?.required ?? false,
        type: addOn?.type ?? CatalogType.QUANTITY,
        price: addOn?.price ?? null,
        priceType: addOn?.priceType ?? AmountType.ABSOLUTE,
        items: sortBy(addOn?.items ?? [], (item) => {
            return item?.sequence ?? Number.NEGATIVE_INFINITY;
        }),
        defaultChoice: addOn?.defaultChoice ?? "",
        products: getSelectionState(selectedExperiences),
        displaySections: defaultDisplaySections,
        restrictions: addOn?.restrictions ?? [],
        quantitySource: addOn?.quantitySource ?? null,
        ...rosterSettings,
    };
};
